/**
 * EntranceExitLogs Realtime Hook
 * 최근 30건 조회 + 자체 WebSocket 실시간 업데이트
 * (Supabase Realtime 대체)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { entranceExitLogsRepository } from '../repositories/EntranceExitLogsRepository';
import * as XLSX from 'xlsx';
import type {
  EntranceExitLog,
  EntranceExitStats,
  LogFilter,
  ActionFilter,
} from '../models/EntranceExitLogs';
import { getTodayKST } from '../../../utils/dateUtils';

const ITEMS_PER_PAGE = 30;

// WebSocket URL 생성
const getWebSocketUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || '';

  if (typeof window !== 'undefined') {
    // HTTPS → WSS, HTTP → WS
    if (window.location.protocol === 'https:') {
      // 프록시 사용 (nginx)
      return `wss://${window.location.host}/ws/entrance-exit-logs`;
    }
  }

  // API URL 기반
  if (apiUrl) {
    return apiUrl.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/entrance-exit-logs';
  }

  return 'ws://localhost:8000/ws/entrance-exit-logs';
};

export const useEntranceExitLogsRealtime = (enabled: boolean = true) => {
  const [logs, setLogs] = useState<EntranceExitLog[]>([]);
  const [stats, setStats] = useState<EntranceExitStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [realtimeMode, setRealtimeMode] = useState<'websocket' | 'polling'>('polling');

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const [filter, setFilter] = useState<LogFilter>({
    startDate: '',
    endDate: '',
    userName: '',
    action: 'all',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /**
   * 로그 조회 (최근 30건)
   */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await entranceExitLogsRepository.getLogs({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        filter,
      });

      if (data?.success) {
        setLogs(data.data.items);
        setTotalLogs(data.data.pagination.total);
        setTotalPages(data.data.pagination.total_pages);

        const entries = data.data.items.filter(log => log.action === 'entry').length;
        const exits = data.data.items.filter(log => log.action === 'exit').length;
        const outings = data.data.items.filter(log => log.action === 'outing').length;

        setStats({
          date: getTodayKST(),
          total_entries: entries,
          total_exits: exits,
          total_outings: outings,
          current_inside: Math.max(0, entries - exits - outings),
          current_inside_members: [],
          hourly_stats: {},
        });
      } else {
        setLogs([]);
        setTotalLogs(0);
        setTotalPages(0);
        setStats(null);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter]);

  /**
   * WebSocket 연결
   */
  const connectWebSocket = useCallback(() => {
    // 이미 연결 중이거나 연결됨
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // 토큰 가져오기 (AuthContext에서 'accessToken'으로 저장)
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('[WebSocket] 토큰 없음, 연결 스킵');
      setRealtimeMode('polling');
      return;
    }

    const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
    console.log('[WebSocket] 연결 시도:', wsUrl.replace(/token=.+/, 'token=***'));

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WebSocket] ✅ 입출입 로그 연결 성공');
        setIsRealtimeConnected(true);
        setRealtimeMode('websocket');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[WebSocket] 메시지 수신:', message.type);

          if (message.type === 'entrance_exit_log') {
            // 새 로그 수신 → 목록 새로고침
            console.log('[WebSocket] 📡 새 입출입 로그:', message.data);
            fetchLogs();
          } else if (message.type === 'connected') {
            console.log('[WebSocket] 연결 확인:', message.message);
          } else if (message.type === 'pong') {
            // ping/pong 응답
          }
        } catch (e) {
          console.warn('[WebSocket] 메시지 파싱 실패:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] 연결 종료:', event.code, event.reason);
        setIsRealtimeConnected(false);
        wsRef.current = null;

        // 재연결 시도
        if (enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`[WebSocket] ${delay}ms 후 재연결 시도 (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        } else {
          console.log('[WebSocket] 폴링 모드로 전환');
          setRealtimeMode('polling');
        }
      };

      ws.onerror = (error) => {
        console.error('[WebSocket] 에러:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] 연결 생성 실패:', error);
      setRealtimeMode('polling');
    }
  }, [enabled, fetchLogs]);

  /**
   * WebSocket 연결 해제
   */
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsRealtimeConnected(false);
  }, []);

  /**
   * 필터 변경 핸들러
   */
  const handleFilterChange = useCallback((newFilter: Partial<LogFilter>) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
    setCurrentPage(1);
  }, []);

  const handleActionFilterChange = useCallback((action: ActionFilter) => {
    handleFilterChange({ action });
  }, [handleFilterChange]);

  const handleSearchChange = useCallback((userName: string) => {
    handleFilterChange({ userName });
  }, [handleFilterChange]);

  const handleResetFilters = useCallback(() => {
    setFilter({
      startDate: '',
      endDate: '',
      userName: '',
      action: 'all',
    });
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  // 날짜 관련 핸들러
  const handleDateChange = useCallback((date: string) => {
    handleFilterChange({ startDate: date, endDate: date });
  }, [handleFilterChange]);

  const handleDateRangeChange = useCallback((type: string) => {
    const today = getTodayKST();
    if (type === 'today') {
      handleFilterChange({ startDate: today, endDate: today });
    } else if (type === 'recent') {
      handleFilterChange({ startDate: '', endDate: '' });
    }
  }, [handleFilterChange]);

  const handleCustomDateRangeChange = useCallback((range: { startDate: string; endDate: string }) => {
    handleFilterChange({ startDate: range.startDate, endDate: range.endDate });
  }, [handleFilterChange]);

  const getDateRange = useCallback(() => {
    return { startDate: filter.startDate, endDate: filter.endDate };
  }, [filter.startDate, filter.endDate]);

  /**
   * 엑셀 다운로드
   */
  const handleExportToExcel = useCallback(async () => {
    try {
      if (!logs.length) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }

      const excelData = logs.map((log, index) => ({
        번호: index + 1,
        시간: new Date(log.timestamp).toLocaleString('ko-KR'),
        회원명: log.user_name,
        회원ID: log.user_id,
        동작: log.action === 'entry' ? '입장' : log.action === 'exit' ? '퇴장' : '외출',
        인식방법: log.method === 'face' ? '얼굴인식' : log.method === 'phone' ? '전화번호' : log.method === 'qr' ? 'QR코드' : log.method,
        게이트: `게이트 ${log.gate_id}`,
        신뢰도: log.confidence ? `${Math.round(log.confidence * 100)}%` : '-',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const wscols = [
        { wch: 5 }, { wch: 20 }, { wch: 12 }, { wch: 38 },
        { wch: 8 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
      ];
      worksheet['!cols'] = wscols;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '입출입기록');

      const today = getTodayKST();
      const filename = `입출입기록_최근30건_${today}.xlsx`;
      XLSX.writeFile(workbook, filename);

      alert(`${logs.length}건의 입출입 기록을 다운로드했습니다.`);
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      alert('엑셀 다운로드에 실패했습니다.');
    }
  }, [logs]);

  // 초기 로그 조회
  useEffect(() => {
    if (!enabled) return;
    fetchLogs();
  }, [enabled, fetchLogs]);

  // WebSocket 연결 및 폴링 설정
  useEffect(() => {
    if (!enabled) {
      disconnectWebSocket();
      setIsRealtimeConnected(false);
      return;
    }

    // WebSocket 연결 시도
    connectWebSocket();

    // 폴링 백업 (WebSocket 연결 실패 시)
    let intervalId: NodeJS.Timeout | null = null;

    const checkAndSetupPolling = () => {
      if (realtimeMode === 'polling' && !filter.userName) {
        intervalId = setInterval(() => {
          fetchLogs();
        }, 30000);
      }
    };

    // WebSocket 연결 시도 후 1초 뒤 폴링 체크
    const pollingCheckTimeout = setTimeout(checkAndSetupPolling, 2000);

    // Cleanup
    return () => {
      clearTimeout(pollingCheckTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
      disconnectWebSocket();
    };
  }, [enabled, connectWebSocket, disconnectWebSocket, filter.userName, fetchLogs, realtimeMode]);

  // Ping/Pong 유지 (30초마다)
  useEffect(() => {
    if (!isRealtimeConnected || !wsRef.current) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isRealtimeConnected]);

  return {
    logs,
    stats,
    loading,
    isRealtimeConnected,
    realtimeMode,
    currentPage,
    totalLogs,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    filter,
    // 날짜 관련 (UI 호환성)
    dateRangeType: 'recent' as const,
    customDateRange: { startDate: '', endDate: '' },
    handleFilterChange,
    handleActionFilterChange,
    handleSearchChange,
    handleResetFilters,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    handleDateChange,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExportToExcel,
    getDateRange,
    selectedDate: '',
    searchName: filter.userName || '',
    actionFilter: filter.action || 'all',
  };
};
